import apiService from './api/ApiService';
import dataCleanUpService from './DataCleanUpService';
import { methods } from './api/ApiConstants';
import navigationService from './NavigationService';
import responseService from './ResponseService';
import { reportError } from '../services/ErrorReporting';
import { t } from '../services/i18nService';
import timeService from './TimeService';
import alertService from '../services/AlertService';
import aclService from '../services/AclService';

import storeManager from '../store/StoreManager';
import responseReader from '../store/readHelpers/ResponseReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import questionReader from '../store/readHelpers/QuestionReader';

import deliveringActions from '../actions/DeliveringActions';
import sessionActions from './../actions/SessionActions';
import sessionReader from '../store/readHelpers/SessionReader';
import parentTestService from './ParentTestService';
import apiReader from '../store/readHelpers/ApiReader';
import responseSendingQueue from './ResponseSendingQueue';
import stateCacheService from './StateCache';
import fileDeliveryUploader from './FileDeliveryUploader';
import connectionStatusService from './ConnectionStatusService';
import connectionStatusConstants from '../constants/ConnectionStatusConstants';
import apiTools, { TIMEOUT_CONNECTION_RESTORED } from './api/ApiTools';
import settingsReader from '../store/readHelpers/SettingsReader';
import liveSessioner from './livesession/LiveSessioner';
import idleWarningService from './IdleWarningService';
import utilService from './UtilService';
import sessionConstants from '../constants/SessionConstants';
import assessmentService from './AssessmentService';
import uiActions from '../actions/UIActions';

/**
 * Compares the state with the status on the server. In all cases where nothing is wrong, the two arrays
 * returned should be empty
 * @param {array} storedItemsStatus The response status on each question on the server
 * @returns {{notInClient: Array, notOnServer: Array}|null}
 */
export function getStateServerDifferences(storedItemsStatus) {
    const differences = {
        notInClient: [],
        notOnServer: [],
    };

    storedItemsStatus.forEach((serverResult) => {
        const contentRevisionId = serverResult.contentRevisionId;
        const question = assessmentReader.getQuestion(contentRevisionId);

        if (!questionReader.canHaveResponse(question)) {
            return;
        }

        const localQuestionIsAnswered = responseReader.isQuestionAttempted(question);
        if (serverResult.isAnswered) {
            if (!localQuestionIsAnswered) {
                // a question is marked as answered on the server but marked as unanswered in the client
                differences.notInClient.push(contentRevisionId);
            }
        } else if (localQuestionIsAnswered) {
            // a question is marked as unanswered on the server but is marked as answered in the client
            differences.notOnServer.push(contentRevisionId);
        }
    });

    if (differences.notInClient.length > 0 || differences.notOnServer.length > 0) {
        return differences;
    }

    return null;
}

async function handleDeliverDone(result) {
    if (!result.success) {
        return Promise.reject({
            errorType: 'Delivering failed',
            response: result,
            error: 'Delivering failed with success false',
        });
    }

    const isCompositeTestContext = sessionReader.isParentTest();

    if (utilService.isIep() && (!isCompositeTestContext || result.wasParentDelivered)) {
        await apiService.sendCompletedFakeRequest();
    }

    storeManager.dispatch(deliveringActions.setActionInProgress(undefined));
    if (!isCompositeTestContext) {
        storeManager.dispatch(deliveringActions.setHasDelivered(true));
    }

    dataCleanUpService.deleteAssessmentAndUserData(
        sessionReader.getAssessmentRunId(),
        sessionReader.getUserId(),
    );

    storeManager.dispatch(
        sessionActions.toggleNavigationLock(false),
    );

    if (!isCompositeTestContext) {
        timeService.cancelSchedules();
    }

    if (isCompositeTestContext) {
        if (sessionReader.supportsLivestream() && result.wasParentDelivered) {
            try {
                liveSessioner.sanitizeLiveSessionsUponDelivery();
            } catch (e) {
                console.log('Unable to sanitize liveSession info', e);
            }
        }
        parentTestService.finishCurrentChildTest(!result.wasParentDelivered); // will reset state.assessment.
        if (sessionReader.shouldStopTimeUponExit()) {
            timeService.stopLastExposedTimeTracking();
        }
        return parentTestService
            .loadParentTestChildTests()
            .then(() => {
                parentTestService.startParentServices()
                    .then(() => {
                        navigationService.navigateToTestOverview();
                    });
            });
    }

    if (settingsReader.isIdleStateWarningEnabled()) {
        idleWarningService.destroy();
    }

    if (sessionReader.shouldStopTimeUponExit()) {
        timeService.stopLastExposedTimeTracking();
    }

    if (aclService.canSeeResultPage()) {
        return responseService
            .fetchUserResponsesAndResetState()
            .then(() => navigationService.navigateToResultPage());
    }

    return navigationService.navigateToDashboardTicket();
}

function handleDeliverInProgress(progressAction) {
    storeManager.dispatch(
        deliveringActions.setActionInProgress(progressAction),
    );

    storeManager.dispatch(
        sessionActions.toggleNavigationLock(true, sessionConstants.LOCK.DELIVER_IN_PROGRESS, null),
    );
}

function handleDeliverFailure() {
    alertService.showAlert(t('deliver.deliverTestFailed'), { timeout: 'none' });
    storeManager.dispatch(
        deliveringActions.setActionInProgress(undefined),
    );
    storeManager.dispatch(
        sessionActions.toggleNavigationLock(false),
    );
}

function handleDeliverNotAvailable() {
    alertService.showDeliveringUnavailableAlert();
    storeManager.dispatch(
        deliveringActions.setActionInProgress(undefined),
    );
    storeManager.dispatch(
        sessionActions.toggleNavigationLock(false),
    );
}

async function deliverWithMethod(method) {
    try {
        const result = await apiService.deliverTest(method);
        if (result.isDeliveringAllowed === false) {
            return handleDeliverNotAvailable();
        }

        return handleDeliverDone(result);
    } catch (error) {
        handleDeliverFailure();
        throw error;
    }
}

async function getStoredItemsDifferences() {
    const { storedItemsStatus } = await apiService.checkStoredItems();
    const differences = getStateServerDifferences(storedItemsStatus);
    return { differences, storedItemsStatus };
}

const deliverService = {
    async checkForResponseState2ServerDifferences() {
        storeManager.dispatch(deliveringActions.resetDeliveringState());
        if (sessionReader.isOfflineMode()) {
            return undefined;
        }

        const { storedItemsStatus } = await apiService.checkStoredItems();
        const differences = getStateServerDifferences(storedItemsStatus);
        if (differences === null) {
            return storeManager.dispatch(deliveringActions.checkStoredItemsCompareSuccess(storedItemsStatus));
        }

        reportError({ message: 'Differences between server and client!', differences });
        return storeManager.dispatch(deliveringActions.checkStoredItemsCompareFailed(storedItemsStatus, differences));
    },

    async clientSideAutoDeliver(assessmentRunId) {
        try {
            await this.waitForResponseQueueToBeEmpty();
            const { differences } = await getStoredItemsDifferences();
            if (differences) {
                reportError({ differences, name: 'CheckStoredItems reported differences!' });
            }
            await this.deliverTest();
        } catch (error) {
            console.error('Error delivering test fallback to offline delivery', assessmentRunId, error);
            timeService.updateTimeRemainingFromCache();
            this.clientSideOfflineDeliver(assessmentRunId);
        }
    },

    getDeliveryDataFromStateCache(assessmentRunId) {
        return {
            deliveryObject: stateCacheService.getStateCacheInstance().getDeliveryObject(assessmentRunId),
            deliveryStatus: stateCacheService.getStateCacheInstance().getDeliveryStatus(assessmentRunId),
        };
    },

    hasClientSideOfflineDeliveryInStateCache(assessmentRunId) {
        const { deliveryObject, deliveryStatus } = this.getDeliveryDataFromStateCache(assessmentRunId);
        return !!deliveryObject && !!deliveryStatus && deliveryStatus !== deliveringActions.statuses.COMPLETE;
    },

    handleClientSideOfflineDeliveryFromStateCache(assessmentRunId) {
        const { deliveryObject, deliveryStatus } = this.getDeliveryDataFromStateCache(assessmentRunId);
        if (deliveryStatus && deliveryObject) {
            storeManager.dispatch(deliveringActions.setOfflineFileDeliveryStatus(assessmentRunId, deliveryStatus));
            storeManager.dispatch(deliveringActions.setOfflineFileDeliveryObject(assessmentRunId, deliveryObject));
            if (deliveryStatus !== deliveringActions.statuses.COMPLETE) {
                fileDeliveryUploader.addToQueue(assessmentRunId, true);
                connectionStatusService.addStatusToQueue(
                    connectionStatusConstants.SYNCHRONISING,
                    TIMEOUT_CONNECTION_RESTORED,
                    apiTools.checkOfflineFileDeliveryStatus.bind(apiTools)
                );
            }
        }
    },

    clientSideOfflineDeliver(assessmentRunId) {
        navigationService.navigateToTestOverview();
        parentTestService.handleOfflineAutoDeliveryOfChildTest(assessmentRunId);
        parentTestService.finishCurrentChildTest();
        parentTestService.startParentServices();
    },

    async waitForResponseQueueToBeEmpty() {
        if (!apiReader.hasUnsavedUserResponses()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const originalQueue = apiReader.getUserResponseQueue();
            const maxTries = originalQueue.length * 5; // 5 seconds per response in the queue.
            const intervalTime = 1000; // ms
            let currentTry = 0;

            responseSendingQueue.getInstance().resetWaitCount();

            const intervalHandle = setInterval(() => {
                currentTry++;
                const currentQueue = apiReader.getUserResponseQueue();
                if (!apiReader.hasUnsavedUserResponses()) {
                    clearInterval(intervalHandle);
                    resolve();
                } else if (!apiReader.isOnline() || !apiReader.isServerOK()) {
                    clearInterval(intervalHandle);
                    reject();
                } else if (currentTry === maxTries) {
                    clearInterval(intervalHandle);
                    reportError(`${currentQueue.length} left in the response saving queue after ${originalQueue.length * 5} seconds.`);
                    reject();
                }
            }, intervalTime);
        });
    },

    async deliverTest() {
        handleDeliverInProgress(deliveringActions.progressActions.SUBMIT);
        return deliverWithMethod(methods.DELIVER_TEST);
    },

    async deliverBlank() {
        handleDeliverInProgress(deliveringActions.progressActions.DELIVER_BLANK);
        return deliverWithMethod(methods.DELIVER_BLANK);
    },

    async withdrawFromTest() {
        handleDeliverInProgress(deliveringActions.progressActions.WITHDRAW);
        return deliverWithMethod(methods.WITHDRAW);
    },

    resetExam() {
        responseService.removeAllItemResults();
        assessmentService.setAllQuestionsAsNotParsed();
        storeManager.dispatch(uiActions.setAnsweredQuestionAutocommit(false));
        dataCleanUpService.deleteAssessmentAndUserData(
            sessionReader.getAssessmentRunId(),
            sessionReader.getUserId(),
        );
        navigationService.navigateToFirstPage();
    },
};

export default deliverService;
